/**
 * Auth Selectors
 *
 * Atomic selectors for authentication state.
 * All selectors return primitives for minimal re-renders.
 *
 * @module store/selectors/authSelectors
 */

import type { GeoMarkupStore } from '../index';

// ===== PRIMITIVE SELECTORS =====

export const selectUser = (state: GeoMarkupStore) => state.auth.user;

export const selectIsAuthenticated = (state: GeoMarkupStore) =>
  state.auth.isAuthenticated;

export const selectIsCheckingAuth = (state: GeoMarkupStore) =>
  state.auth.isCheckingAuth;

export const selectWorkspaces = (state: GeoMarkupStore) =>
  state.auth.workspaces;

export const selectCurrentWorkspaceId = (state: GeoMarkupStore) =>
  state.auth.currentWorkspaceId;

// ===== DERIVED SELECTORS =====

export const selectCurrentWorkspace = (state: GeoMarkupStore) =>
  state.auth.workspaces.find(
    (workspace) => workspace.id === state.auth.currentWorkspaceId
  );

export const selectUserRole = (state: GeoMarkupStore) =>
  selectCurrentWorkspace(state)?.role ?? null;

export const selectIsAdmin = (state: GeoMarkupStore) => {
  const workspace = selectCurrentWorkspace(state);
  // Admin or owner has admin privileges
  return workspace?.role === 'Admin' || workspace?.isOwner === true;
};

export const selectSubscriptionTier = (state: GeoMarkupStore) =>
  state.auth.user?.subscriptionTier ?? 'Free';

export const selectSubscriptionValidUntil = (state: GeoMarkupStore) =>
  state.auth.user?.subscriptionValidUntil ?? null;

export const selectIsPaidSubscription = (state: GeoMarkupStore) => {
  const tier = selectSubscriptionTier(state);
  return tier === 'Basic' || tier === 'Pro';
};

export const selectSessionExpired = (state: GeoMarkupStore) =>
  state.auth.sessionExpired;

/**
 * Whether the active workspace's map has been configured.
 * Reads the lightweight `/auth/me` session flag (auth.workspaces) so the
 * first-run gate can decide before extended workspace data has loaded.
 */
export const selectActiveWorkspaceIsMapConfigured = (
  state: GeoMarkupStore
): boolean =>
  state.auth.workspaces.find(
    (workspace) => workspace.id === state.auth.currentWorkspaceId
  )?.isMapConfigured ?? false;
